import { Map } from 'immutable'
import { SECTION_CSV_HEADERS } from './constants'
import { extractSectionData } from './extractSectionData'

/**
 * Converts an array of rows into a CSV string
 * @param {Array<string>} headers - The CSV headers
 * @param {Array<Array<any>>} rows - The data rows
 * @returns {string} The CSV content
 */
function generateCSVContent (headers, rows) {
  const escapeCell = (cell) => {
    const escaped = String(cell).replace(/"/g, '""')
    return escaped.includes(',') ? `"${escaped}"` : escaped
  }
  return [headers.join(','), ...rows.map((row) => row.map(escapeCell).join(','))].join('\n')
}

/**
 * Creates a CSV string from the provided sections (using extractSectionData and generateCSVContent).
 * (Note: This function does not trigger a download; use downloadCSV (from csvUtils) for that.)
 *
 * @param {Map} sections – An immutable Map of sections (keyed by section id)
 * @param {List} sectionIds – An ordered list of section ids (used to build the CSV rows)
 * @param {Object} auth – (Optional) auth object (used by extractSectionData if needed)
 * @param {Object} typeIdToType – Mapping of typeId to type (used to build typeIdToSlug)
 * @param {Object} listIdToName – (Optional) Mapping of listId (e.g. from "Content list") to human-readable name
 * @param {Object} contentIdToTitle – Mapping of contentId (e.g. from "Content filters") to human-readable title
 * @param {Map} pmNodes – Immutable Map (keyed by content id) of pmNodes (used for "Has Watched" titles)
 * @param {Object} rainbowCategoryIdToLabel – Mapping of rainbowCategoryId to label
 * @returns {string} – The CSV string (as generated by generateCSVContent)
 */
export function createCSV (
  sections,
  sectionIds,
  auth,
  typeIdToType = {},
  listIdToName = {},
  contentIdToTitle = {},
  pmNodes = Map(),
  rainbowCategoryIdToLabel = {},
) {
  if (!Map.isMap(sections)) {
    throw new Error('The sections argument is required to be an immutable Map')
  }

  // Convert sections (using extractSectionData) into rows (in the order of sectionIds)
  const rows = sectionIds
    .map((id) => {
      const section = sections.get(id)
      if (!section) return null
      return extractSectionData(
        section,
        pmNodes,
        typeIdToType,
        listIdToName,
        contentIdToTitle,
        rainbowCategoryIdToLabel,
      )
    })
    .filter(Boolean)

  // Generate the CSV string (using generateCSVContent) and return it
  return generateCSVContent(SECTION_CSV_HEADERS, rows)
}
